import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFinancialVisibility } from '../hooks/useFinancialVisibility';
import usePermissions from '../hooks/usePermissions';
import { Button } from './ui/button';
import MVMLogo from './MVMLogo';
import UpdateBanner from './UpdateBanner';
import ThemeToggle from './ThemeToggle';
import { 
  Building2, 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Package, 
  User, 
  LogOut, 
  Menu,
  X,
  CalendarDays,
  Crown,
  Database,
  CreditCard,
  Bell,
  Receipt,
  Landmark,
  ChevronRight,
  Share2,
  Mail,
  MessageCircle,
  Copy,
  Check,
  FileText
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const hideFinancials = useFinancialVisibility();
  const { currentUser, logout, impersonating, masterUser, returnToMaster, refreshUser } = useAuth();
  const { can: canDo, loading: permsLoading } = usePermissions();

  // Refresh user data when component mounts or when navigating to subscription page
  useEffect(() => {
    // Refresh user data on mount to get latest subscription status
    if (currentUser && !currentUser.isMaster) {
      refreshUser();
    }
  }, [location.pathname]);

  // Build menu items based on user role
  const baseMenuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/venues', icon: Building2, label: 'Venues' },
    { path: '/bookings', icon: CalendarDays, label: 'Bookings' },
    { path: '/leads', icon: Users, label: 'Leads' },
    { path: '/calendar', icon: Calendar, label: 'Calendar' },
    { path: '/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/quotations', icon: FileText, label: 'Quotations' },
    { path: '/inventory', icon: Package, label: 'Inventory' },
    { path: '/accounts', icon: Landmark, label: 'Accounts' },
    { path: '/profile', icon: User, label: 'Profile' },
    { path: '/subscription', icon: CreditCard, label: 'Subscription' }
  ];

  // Receptionist role only sees Calendar and Profile
  const receptionistMenuItems = [
    { path: '/calendar', icon: Calendar, label: 'Calendar' },
    { path: '/profile', icon: User, label: 'Profile' }
  ];

  // Build menu based on user role
  let menuItems;
  
  if (currentUser?.role === 'receptionist') {
    // Receptionist only sees Calendar page
    menuItems = receptionistMenuItems;
  } else if (currentUser?.role === 'master_panel') {
    // Master Panel users only see the control panel
    menuItems = [
      { path: '/master-control-panel', icon: Crown, label: 'Control Panel' },
      { path: '/profile', icon: User, label: 'Profile' },
    ];
  } else if (currentUser?.isMaster) {
    menuItems = [
      { path: '/master', icon: Crown, label: 'Master Control', isMasterOnly: true },
      { path: '/master-data', icon: Database, label: 'Master Data', isMasterOnly: true },
      { path: '/master-control-panel', icon: CreditCard, label: 'Subscription Panel', isMasterOnly: true },
      { path: '/subscriptions', icon: CreditCard, label: 'Subscriptions', isMasterOnly: true },
      ...baseMenuItems.filter(item => item.path !== '/subscription') // Master already has Subscriptions
    ];
  } else {
    menuItems = baseMenuItems;
  }

  // Permission-gate the sidebar for staff roles (admin/master see everything)
  const isStaffRole = ['staff', 'manager', 'stakeholder'].includes(currentUser?.role);
  if (isStaffRole && !permsLoading) {
    const menuPermMap = {
      '/venues': ['view', 'venues'],
      '/bookings': ['view', 'bookings'],
      '/leads': ['view', 'leads'],
      '/invoices': ['view', 'invoices'],
      '/quotations': ['view', 'invoices'],
      '/inventory': ['view', 'inventory'],
      '/accounts': ['view', 'accounts']
    };
    menuItems = menuItems.filter(item => {
      if (item.path === '/subscription') return false; // Billing is admin-only
      const req = menuPermMap[item.path];
      if (!req) return true; // Dashboard, Calendar, Profile always visible
      return canDo(req[0], req[1]);
    });
  }
  
  // Hide Invoices tab when module is disabled for this admin
  // Invoices is opt-in: hide unless enabledModules explicitly includes 'invoices'
  // Master admin always sees it
  const enabledModules = currentUser?.enabledModules;
  if (!currentUser?.isMaster) {
    if (!Array.isArray(enabledModules) || !enabledModules.includes('invoices')) {
      menuItems = menuItems.filter(item => item.path !== '/invoices');
    }
  }

  // Hide Quotations tab when module is disabled for this admin
  // Quotations is opt-in: hide unless enabledModules explicitly includes 'quotations'
  // Master admin is always allowed to see Quotations for support/demo purposes
  if (!currentUser?.isMaster) {
    if (!Array.isArray(enabledModules) || !enabledModules.includes('quotations')) {
      menuItems = menuItems.filter(item => item.path !== '/quotations');
    }
  }

  // Hide Accounts tab when hideFinancials is enabled
  if (hideFinancials) {
    menuItems = menuItems.filter(item => item.path !== '/accounts');
  }

  const handleLogout = async () => {
    // Logout will clear all cache automatically
    await logout();
    
    // Show brief message
    
    // Navigate to login
    navigate('/login');
  };

  // Get notifications for current user
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loadingNotifications, setLoadingNotifications] = React.useState(false);

  // Load notifications from backend
  const loadNotifications = React.useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoadingNotifications(true);
      const { notificationsAPI } = await import('../services/api');
      const [notifs, countData] = await Promise.all([
        notificationsAPI.getAll(),
        notificationsAPI.getUnreadCount()
      ]);
      // Sort notifications: unread first, then by date (newest first)
      const sortedNotifs = [...notifs].sort((a, b) => {
        // First sort by read status (unread first)
        if (a.read !== b.read) return a.read ? 1 : -1;
        // Then sort by date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setNotifications(sortedNotifs);
      setUnreadCount(countData.count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      // Fallback to empty notifications on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoadingNotifications(false);
    }
  }, [currentUser]);

  React.useEffect(() => {
    loadNotifications();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Close notification dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on notification dropdown or mobile notification sheet
      if (notificationOpen && 
          !event.target.closest('.notification-dropdown') && 
          !event.target.closest('.notification-mobile-sheet')) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationOpen]);

  const markAsRead = async (notificationId) => {
    try {
      const { notificationsAPI } = await import('../services/api');
      await notificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { notificationsAPI } = await import('../services/api');
      await notificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      const { notificationsAPI } = await import('../services/api');
      await notificationsAPI.clearAll();
      
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'payment_followup':
        return '💰';
      case 'subscription_approved':
      case 'payment_success':
        return '✅';
      case 'subscription_rejected':
        return '❌';
      case 'subscription_extended':
        return '📅';
      case 'subscription_expired':
        return '⚠️';
      default:
        return '🔔';
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setSelectedNotification(notification);
  };

  const closeNotificationDetail = () => {
    setSelectedNotification(null);
  };

  // Get subscription badge based on plan
  const getSubscriptionBadge = () => {
    if (!currentUser) return null;
    
    // Master admin badge
    if (currentUser.isMaster) {
      return {
        name: 'Master Admin',
        icon: '👑',
        gradient: 'from-purple-600 via-purple-500 to-indigo-600',
        textColor: 'text-white',
        border: 'border-purple-400/50',
        shadow: 'shadow-lg shadow-purple-500/50'
      };
    }

    const subscription = currentUser.subscription;
    if (!subscription || !subscription.plan) {
      return {
        name: 'No Plan',
        icon: '⚪',
        gradient: 'from-gray-500 to-gray-600',
        textColor: 'text-white',
        border: 'border-gray-400/50',
        shadow: 'shadow-md shadow-gray-500/30'
      };
    }

    // Badge configurations for each plan
    const badges = {
      'PLAN_TRIAL': {
        name: 'Trial',
        icon: '🎁',
        gradient: 'from-green-500 to-emerald-600',
        textColor: 'text-white',
        border: 'border-green-400/50',
        shadow: 'shadow-md shadow-green-500/30'
      },
      'PLAN_1_MONTH': {
        name: '1 Month',
        icon: '⭐',
        gradient: 'from-blue-500 to-cyan-500',
        textColor: 'text-white',
        border: 'border-blue-400/50',
        shadow: 'shadow-md shadow-blue-500/30'
      },
      'PLAN_3_MONTHS': {
        name: '3 Months',
        icon: '💎',
        gradient: 'from-orange-500 via-amber-500 to-yellow-500',
        textColor: 'text-white',
        border: 'border-orange-400/50',
        shadow: 'shadow-md shadow-orange-500/30'
      },
      'PLAN_6_MONTHS': {
        name: '6 Months',
        icon: '🏅',
        gradient: 'from-pink-500 via-rose-500 to-red-500',
        textColor: 'text-white',
        border: 'border-pink-400/50',
        shadow: 'shadow-md shadow-pink-500/30'
      },
      'PLAN_12_MONTHS': {
        name: '12 Months',
        icon: '🏆',
        gradient: 'from-amber-400 via-yellow-500 to-orange-500',
        textColor: 'text-white',
        border: 'border-amber-400/50',
        shadow: 'shadow-lg shadow-amber-500/40'
      },
      'PLAN_5_YEARS': {
        name: '5 Years',
        icon: '👑',
        gradient: 'from-violet-600 via-purple-500 to-indigo-600',
        textColor: 'text-white',
        border: 'border-violet-400/50',
        shadow: 'shadow-xl shadow-purple-500/50'
      }
    };

    // Map old plan IDs to new ones for backward compatibility
    const planIdMap = {
      'PLAN_FREE_TRIAL': 'PLAN_TRIAL',
      'PLAN_FREE': 'PLAN_TRIAL',
      'PLAN_BASIC': 'PLAN_1_MONTH',
      'PLAN_PRO': 'PLAN_3_MONTHS',
      'PLAN_LIFETIME': 'PLAN_5_YEARS',
      'Professional': 'PLAN_3_MONTHS',
      'Enterprise': 'PLAN_12_MONTHS',
      'Basic': 'PLAN_1_MONTH'
    };

    const planId = subscription.plan;
    const mappedPlanId = planIdMap[planId] || planId;
    const badge = badges[mappedPlanId];

    if (badge) return badge;

    // Default fallback badge
    return {
      name: 'Active',
      icon: '✓',
      gradient: 'from-blue-500 to-cyan-500',
      textColor: 'text-white',
      border: 'border-blue-300/50',
      shadow: 'shadow-md shadow-blue-500/30'
    };
  };

  const badge = getSubscriptionBadge();

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-background">
      {/* Update Banner - Shows when new version is available */}
      <UpdateBanner />
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 
          bg-gradient-to-b from-teal-600 via-teal-700 to-cyan-700 
          dark:from-[hsl(0,0%,13%)] dark:via-[hsl(0,0%,10%)] dark:to-[hsl(0,0%,8%)]
          text-white transform transition-transform duration-200 ease-out will-change-transform
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-teal-500 dark:border-[hsl(25,20%,22%)]">
            <div className="flex items-center space-x-3">
              <MVMLogo size="sm" />
              <div>
                <h1 className="text-xl font-bold">MyVenueMate</h1>
                <p className="text-xs text-teal-200 dark:text-[hsl(30,5%,60%)]">Venue Management Made Easy</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:bg-white/10 p-1 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 bg-white/10 dark:bg-[hsl(25,54%,45%)]/10 backdrop-blur-sm">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center font-bold">
                {currentUser?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{currentUser?.name}</p>
                <p className="text-xs text-teal-200 dark:text-[hsl(30,5%,60%)] capitalize">{currentUser?.role}</p>
              </div>
            </div>
            
            {/* Subscription Badge - Hidden for staff users */}
            {currentUser?.role !== 'staff' && (
              <div className={`
                inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg
                bg-gradient-to-r ${badge.gradient}
                border ${badge.border}
                ${badge.shadow}
                transform hover:scale-105 transition-transform duration-200
              `}>
                <span className="text-lg">{badge.icon}</span>
                <span className={`text-xs font-bold ${badge.textColor} tracking-wide`}>
                  {badge.name}
                </span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg 
                    transition-all duration-200
                    ${isActive 
                      ? item.isMasterOnly 
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 shadow-lg' 
                        : 'bg-white/20 shadow-lg'
                      : item.isMasterOnly
                        ? 'hover:bg-purple-500/20 border border-purple-400/30'
                        : 'hover:bg-white/10'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.isMasterOnly && (
                    <Crown className="w-4 h-4 ml-auto" />
                  )}
                </Link>
              );
            })}
            
            {/* Share App Button */}
            <button
              onClick={() => setShareModalOpen(true)}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 hover:bg-white/10 mt-2 border border-dashed border-white/30"
            >
              <Share2 className="w-5 h-5" />
              <span className="font-medium">Share App</span>
            </button>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-teal-500 dark:border-[hsl(25,20%,22%)]">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
            
            {/* Powered by */}
            <div className="mt-4 text-center">
              <p className="text-xs text-teal-200 dark:text-[hsl(30,5%,60%)]">
                Powered by <span className="font-semibold text-white">MYONK</span>
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Impersonation Banner */}
        {impersonating && (
          <div className="bg-purple-600 text-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">
                Viewing as: {currentUser?.name} ({currentUser?.role})
              </span>
            </div>
            <Button
              onClick={() => {
                returnToMaster();
                navigate('/subscriptions');
              }}
              size="sm"
              className="bg-white text-purple-600 hover:bg-purple-50"
            >
              Return to Master Account
            </Button>
          </div>
        )}

        {/* Header */}
        <header className="bg-white dark:bg-card border-b border-gray-200 dark:border-border px-3 sm:px-6 py-3 sm:py-4 relative z-50">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white shrink-0 p-1"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-foreground truncate">
                {menuItems.find(item => item.path === location.pathname)?.label || 'MyVenueMate'}
              </h2>
            </div>
            
            {/* Theme Toggle */}
            <ThemeToggle className="shrink-0" />
            
            {/* Notification Bell */}
            <div className="relative notification-dropdown shrink-0" style={{ zIndex: 9999 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNotificationOpen(!notificationOpen);
                }}
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-[10px] sm:text-xs font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown - Desktop Only (Mobile is rendered outside header) */}
              {notificationOpen && (
                <>
                  {/* Desktop: Regular dropdown */}
                  <div 
                    className="hidden sm:block absolute right-0 mt-2 w-96 bg-white dark:bg-card rounded-lg shadow-2xl border border-gray-200 dark:border-border max-h-96 overflow-hidden z-[99999]"
                  >
                    <div className="p-4 border-b border-gray-200 dark:border-border flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-foreground">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-teal-600 dark:text-primary hover:text-teal-700 font-medium"
                          >
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={clearAllNotifications}
                            className="text-xs text-red-500 hover:text-red-600 font-medium"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="overflow-y-auto max-h-80">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-muted-foreground">
                          <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-muted" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => markAsRead(notification.id)}
                            className={`p-4 border-b border-gray-100 dark:border-border hover:bg-gray-50 dark:hover:bg-secondary cursor-pointer transition-colors ${
                              !notification.read ? 'bg-teal-50 dark:bg-primary/10' : ''
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <span className="text-2xl flex-shrink-0">
                                {getNotificationIcon(notification.type)}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <p className="font-semibold text-sm text-gray-900 dark:text-foreground">
                                    {notification.title}
                                  </p>
                                  {!notification.read && (
                                    <span className="ml-2 w-2 h-2 bg-teal-500 dark:bg-primary rounded-full flex-shrink-0"></span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-muted-foreground mt-2">
                                  {new Date(notification.createdAt).toLocaleString('en-IN', {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-4 md:p-6 relative z-0 -webkit-overflow-scrolling-touch dark:bg-background">
          {children}
        </main>
      </div>

      {/* Mobile Notification Sheet - Rendered outside header for proper z-index */}
      {notificationOpen && (
        <>
          {/* Overlay - Mobile only */}
          <div 
            className="sm:hidden notification-mobile-sheet fixed inset-0 bg-black/50"
            style={{ zIndex: 99998 }}
            onClick={() => {
              setNotificationOpen(false);
              setSelectedNotification(null);
            }} 
          />
          {/* Bottom Sheet - Mobile only */}
          <div 
            className="sm:hidden notification-mobile-sheet fixed left-0 right-0 bottom-0 bg-white dark:bg-card rounded-t-2xl shadow-2xl"
            style={{ zIndex: 99999, maxHeight: '80vh' }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-muted rounded-full" />
            </div>
            
            {/* Notification Detail View */}
            {selectedNotification ? (
              <>
                <div className="p-4 border-b border-gray-200 dark:border-border flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 dark:text-foreground text-lg">Notification Details</h3>
                  <button 
                    onClick={() => {
                      setNotificationOpen(false);
                      setSelectedNotification(null);
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-secondary rounded-full"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-muted-foreground" />
                  </button>
                </div>
                
                <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 160px)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl">
                      {getNotificationIcon(selectedNotification.type)}
                    </span>
                    <div>
                      <h4 className="font-bold text-lg text-gray-900 dark:text-foreground">
                        {selectedNotification.title}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground">
                        {new Date(selectedNotification.createdAt).toLocaleString('en-IN', {
                          dateStyle: 'full',
                          timeStyle: 'short'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4">
                    <p className="text-gray-700 dark:text-foreground text-sm leading-relaxed whitespace-pre-wrap">
                      {selectedNotification.message}
                    </p>
                  </div>
                  
                  {selectedNotification.metadata && (
                    <div className="mt-4 p-3 bg-teal-50 dark:bg-primary/10 rounded-lg">
                      <p className="text-xs text-teal-700 dark:text-primary font-medium">Additional Info</p>
                      <p className="text-sm text-teal-600 dark:text-primary/80 mt-1">
                        {JSON.stringify(selectedNotification.metadata, null, 2)}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="p-4 border-t border-gray-200 dark:border-border">
                  <button
                    onClick={closeNotificationDetail}
                    className="w-full py-3 bg-gray-100 dark:bg-secondary hover:bg-gray-200 dark:hover:bg-muted text-gray-700 dark:text-foreground font-medium rounded-lg transition-colors"
                  >
                    Back to Notifications
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Notification List View */}
                <div className="p-4 border-b border-gray-200 dark:border-border flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 dark:text-foreground text-lg">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-teal-600 dark:text-primary hover:text-teal-700 font-medium"
                      >
                        Mark read
                      </button>
                    )}
                    {notifications.length > 0 && (
                      <button
                        onClick={clearAllNotifications}
                        className="text-xs text-red-500 hover:text-red-600 font-medium"
                      >
                        Clear all
                      </button>
                    )}
                    <button 
                      onClick={() => setNotificationOpen(false)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-secondary rounded-full"
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-muted-foreground" />
                    </button>
                  </div>
                </div>
                
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 100px)' }}>
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-muted-foreground">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-muted" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`p-4 border-b border-gray-100 dark:border-border active:bg-gray-100 dark:active:bg-secondary cursor-pointer transition-colors ${
                          !notification.read ? 'bg-teal-50 dark:bg-primary/10' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl flex-shrink-0">
                            {getNotificationIcon(notification.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <p className="font-semibold text-sm text-gray-900 dark:text-foreground pr-2">
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-teal-500 dark:bg-primary rounded-full flex-shrink-0 mt-1"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-muted-foreground mt-2">
                              {new Date(notification.createdAt).toLocaleString('en-IN', {
                                dateStyle: 'medium',
                                timeStyle: 'short'
                              })}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-muted-foreground flex-shrink-0 mt-1" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Share App Modal */}
      {shareModalOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-[99998]"
            onClick={() => {
              setShareModalOpen(false);
              setLinkCopied(false);
            }}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
            <div className="bg-white dark:bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 dark:from-primary dark:to-primary/80 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Share2 className="w-6 h-6 text-white" />
                    <h3 className="text-lg font-bold text-white">Share MyVenueMate</h3>
                  </div>
                  <button 
                    onClick={() => {
                      setShareModalOpen(false);
                      setLinkCopied(false);
                    }}
                    className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <p className="text-teal-100 dark:text-white/70 text-sm mt-1">Share the app brochure with your contacts</p>
              </div>
              
              {/* Share Options */}
              <div className="p-6 space-y-4">
                {/* WhatsApp - Using intent URL that works on both mobile and desktop */}
                <button
                  onClick={() => {
                    const shareText = 'Check out MyVenueMate - The best venue management platform!\n\nDownload Brochure: https://myvenuemate.in/api/visitor/download/brochure\n\nVisit: https://myvenuemate.in';
                    
                    // Try native share first (works on mobile)
                    if (navigator.share) {
                      navigator.share({
                        title: 'MyVenueMate - Venue Management Platform',
                        text: shareText,
                        url: 'https://myvenuemate.in'
                      }).catch(() => {
                        // Fallback: copy to clipboard
                        navigator.clipboard.writeText(shareText);
                        alert('Message copied! Paste it in WhatsApp to share.');
                      });
                    } else {
                      // Desktop fallback: copy message and open WhatsApp Web
                      navigator.clipboard.writeText(shareText);
                      alert('Message copied to clipboard!\n\nClick OK to open WhatsApp Web, then paste the message (Ctrl+V).');
                      window.open('https://web.whatsapp.com/', '_blank');
                    }
                  }}
                  className="w-full flex items-center space-x-4 p-4 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-xl transition-colors group text-left"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-foreground">WhatsApp</p>
                    <p className="text-sm text-gray-500 dark:text-muted-foreground">Share via WhatsApp</p>
                  </div>
                </button>
                
                {/* Email */}
                <a
                  href={`mailto:?subject=${encodeURIComponent('Check out MyVenueMate - Venue Management Platform')}&body=${encodeURIComponent('Hi,\n\nI wanted to share this amazing venue management platform with you - MyVenueMate!\n\nIt helps manage bookings, leads, payments, and inventory for wedding halls, banquet halls, and event venues.\n\nDownload Brochure: https://myvenuemate.in/api/visitor/download/brochure\n\nVisit: https://myvenuemate.in\n\nBest regards')}`}
                  className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-colors group"
                >
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-foreground">Email</p>
                    <p className="text-sm text-gray-500 dark:text-muted-foreground">Share via Email</p>
                  </div>
                </a>
                
                {/* Download Brochure */}
                <a
                  href="/api/visitor/download/brochure"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-4 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-xl transition-colors group"
                >
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-foreground">Download Brochure</p>
                    <p className="text-sm text-gray-500 dark:text-muted-foreground">Get PDF brochure</p>
                  </div>
                </a>
                
                {/* Copy Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('https://myvenuemate.in/api/visitor/download/brochure');
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 3000);
                  }}
                  className="w-full flex items-center space-x-4 p-4 bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:hover:bg-muted rounded-xl transition-colors group"
                >
                  <div className={`w-12 h-12 ${linkCopied ? 'bg-green-500' : 'bg-gray-500 dark:bg-muted-foreground'} rounded-full flex items-center justify-center group-hover:scale-110 transition-all`}>
                    {linkCopied ? <Check className="w-6 h-6 text-white" /> : <Copy className="w-6 h-6 text-white" />}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-foreground">{linkCopied ? 'Link Copied!' : 'Copy Link'}</p>
                    <p className="text-sm text-gray-500 dark:text-muted-foreground">{linkCopied ? 'Share it anywhere' : 'Copy brochure link to clipboard'}</p>
                  </div>
                </button>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-secondary border-t border-gray-100 dark:border-border">
                <p className="text-xs text-center text-gray-500 dark:text-muted-foreground">
                  Share MyVenueMate with venue owners and help them manage their business better!
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Layout;